import { Schema } from "@effect/schema";
import {
  Address,
  ChainPublicClientService,
  MarketUserState,
  Percent,
  PercentFromBigInt,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { ContractsClientError } from "./types";
import { getContract } from "viem";
import { siloLensAddress } from "@silo/contract-addresses";
import { siloLensAbi } from "@silo/contract-abi-types";
import {
  requireAnySettled,
  settledOrZero,
} from "../utils/settle-reverts";
import {
  getBorrowingPowerUsed,
  getHealthFactor,
  getIsAtRiskOfLiquidation,
} from "@silo/utils";

export type GetMarketUserState = ReturnType<typeof getMarketUserState>;
export type GetMarketUserStateSuccess =
  Effect.Effect.Success<GetMarketUserState>;
export type GetMarketUserStateError = Effect.Effect.Error<GetMarketUserState>;
export type GetMarketUserStateContext =
  Effect.Effect.Context<GetMarketUserState>;

interface GetMarketUserStateParams {
  marketAddress: Address;
  user: Address;
  ltArray: Percent[];
}

export function getMarketUserState({
  marketAddress,
  user,
  ltArray,
}: GetMarketUserStateParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;

    const siloLensContract = getContract({
      address: siloLensAddress[protocolKey],
      abi: siloLensAbi,
      client: publicClient,
    });

    const viemMarketUserStateEncoded = yield* Effect.tryPromise({
      try: async () => {
        const results = requireAnySettled(
          await Promise.allSettled([
            siloLensContract.read.getUserLTV([marketAddress, user]),
            siloLensContract.read.getUserLiquidationThreshold([
              marketAddress,
              user,
            ]),
            siloLensContract.read.getUserMaximumLTV([marketAddress, user]),
          ])
        );

        const [userLtv, userLiquidationThreshold, userMaxLtv] =
          results.map(settledOrZero);

        const encoded: ViemMarketUserStateEncoded = {
          userLtv,
          userLiquidationThreshold,
          userMaxLtv,
        };

        return encoded;
      },
      catch: (cause) =>
        new ContractsClientError({
          cause,
          meta: { user, marketAddress },
        }),
    });

    const { userLtv, userLiquidationThreshold, userMaxLtv } = yield* pipe(
      viemMarketUserStateEncoded,
      Schema.decode(ViemMarketUserState)
    );

    const healthFactor = getHealthFactor(userLtv, userLiquidationThreshold);
    const borrowingPowerUsed = getBorrowingPowerUsed(userLtv, userMaxLtv);
    const isAtRiskOfLiquidation = getIsAtRiskOfLiquidation(
      healthFactor,
      ltArray
    );

    const marketUserState: MarketUserState = {
      userLtv,
      userLiquidationThreshold,
      healthFactor,
      borrowingPowerUsed,
      isAtRiskOfLiquidation,
    };

    return marketUserState;
  });
}

export type ViemMarketUserState = typeof ViemMarketUserState.Type;
export type ViemMarketUserStateEncoded = typeof ViemMarketUserState.Encoded;

export const ViemMarketUserState = Schema.Struct({
  userLtv: PercentFromBigInt,
  userLiquidationThreshold: PercentFromBigInt,
  userMaxLtv: PercentFromBigInt,
}).annotations({ identifier: "ViemMarketUserState" });
